import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Button,
  Checkbox,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import {
  createOrUpdateScheduledTest,
  deleteScheduledTestForUser,
} from "../../../../ApiLayer/scheduledTestProvider";

const ScheduledTestModal = ({
  isOpen,
  handleClose,
  testData = {},
  userId,
  editId,
  tests,
}) => {
  const [scheduledDate, setScheduledDate] = useState(null);
  const [testType, setTestType] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isMandatory, setIsMandatory] = useState(false);
  const [noticePeriod, setNoticePeriod] = useState(null);
  const [update, setUpdate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (testData && testData.start) {
      setScheduledDate(new Date(testData.start));
    } else {
      setScheduledDate(null);
    }
    setIsMandatory(testData ? testData.isMandatory : false);
    setNoticePeriod(testData ? testData.noticePeriod : 0);
    setTestType(testData ? testData.title : null);
  }, [testData]);

  const handleCreateOrUpdate = async () => {
    setIsLoading(true);

    const success = await createOrUpdateScheduledTest(
      userId,
      editId,
      tests,
      testType,
      scheduledDate,
      isMandatory,
      noticePeriod
    );

    setIsLoading(false);

    if (success) {
      setTestType(null);
      handleClose();
    }
  };

  const handleScheduledDateChange = (date) => {
    setScheduledDate(date);
  };

  const handleTestTypeChange = (event) => {
    const selectedTestName = event.target.value;
    const selectedTest = tests.find((test) => test.name === selectedTestName);
    setTestType(selectedTestName);
    setIsMandatory(selectedTest ? selectedTest.is_mandatory : false);
  };

  const closeHandler = () => {
    setTestType(null);
    setScheduledDate(null);
    handleClose();
  };
  const handleDeleteConfirmClose = () => setDeleteConfirmOpen(false);
  const handleDeleteConfirmOpen = () => setDeleteConfirmOpen(true);

  const handleDelete = async () => {
    if (editId) {
      const deleteSuccess = await deleteScheduledTestForUser(userId, editId);

      if (deleteSuccess) {
        // Schedule the closing of the dialog after the delete request succeeds
        handleDeleteConfirmClose();
        // Reset the deleteId after successful deletion
        setUpdate(!update);
        handleClose();
      }
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={closeHandler}
        aria-labelledby="test-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="test-dialog-title" style={{ position: "relative" }}>
          {testData?.completionDate
            ? ""
            : editId
            ? "Edit Test"
            : "Add New Test"}
          <IconButton
            style={{ position: "absolute", right: 0, top: 0 }}
            onClick={closeHandler}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div style={{ margin: "10px 0", width: "100%" }}>
              <DatePicker
                label="Scheduled Date"
                onChange={handleScheduledDateChange}
                disabled={!!testData?.completionDate}
                value={
                  testData?.scheduledStart
                    ? dayjs(testData.scheduledStart)
                    : scheduledDate
                    ? dayjs(scheduledDate)
                    : null
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
                slotProps={{ textField: { fullWidth: true } }}
                style={{ width: "100%" }}
              />
            </div>
          </LocalizationProvider>
          <FormControl fullWidth>
            <InputLabel id="test-type-label">Test Type</InputLabel>
            <Select
              labelId="test-type-label"
              id="test-type"
              disabled={!!testData?.completionDate}
              value={testType}
              onChange={handleTestTypeChange}
              label="Test Type"
            >
              {tests.map((test) => (
                <MenuItem key={test.id} value={test.name}>
                  {test.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            style={{ marginBottom: "15px" }}
            control={
              <Checkbox
                disabled={!!testData?.completionDate}
                checked={isMandatory ? true : false}
                onChange={(event) => setIsMandatory(event.target.checked)}
              />
            }
            label="Is Mandatory?"
          />

          {isMandatory && (
            <FormControl fullWidth style={{ marginBottom: "15px" }}>
              <InputLabel id="grace-period-label">Grace Period</InputLabel>

              <Select
                labelId="grace-period-label"
                id="grace-period"
                label="Grace Period"
                disabled={!!testData?.completionDate}
                value={noticePeriod}
                onChange={(event) => setNoticePeriod(event.target.value)}
                defaultValue={0} // Set the default value here
              >
                <MenuItem value={0}>0 days</MenuItem>
                <MenuItem value={1}>1 day</MenuItem>
                <MenuItem value={2}>2 days</MenuItem>
                <MenuItem value={3}>3 days</MenuItem>
                <MenuItem value={4}>4 days</MenuItem>
                <MenuItem value={5}>5 days</MenuItem>
              </Select>
            </FormControl>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "15px",
            }}
          >
            {!testData?.completionDate &&
              (editId ? (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateOrUpdate}
                    style={{ width: "48%" }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    style={{
                      width: "48%",
                      backgroundColor: "red",
                      color: "white",
                    }}
                    onClick={handleDeleteConfirmOpen}
                  >
                    Delete
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateOrUpdate}
                  style={{ width: "100%" }}
                  disabled={!testType || isLoading}
                >
                  {isLoading ? "Processing..." : "Create"}
                </Button>
              ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose}>
        <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this test?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose} color="primary">
            No
          </Button>
          <Button onClick={handleDelete} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScheduledTestModal;
